import { useEffect, useMemo, useState } from 'react';
import { FiShield, FiTrash2 } from 'react-icons/fi';
import { TableHeader, TablePerPage } from '../../../../../_CORE/APP/Templates/_UI/Table/_index';
import { FooterPagination } from '../../../../../_CORE/APP/Templates/Layouts/Section/Table/_Plugins/FooterPagination';
import { EmailSectionLayout } from '../_UI/EmailSectionLayout';
import { EmailFieldHint, EmailFieldLabel } from '../_UI/EmailFieldGuide';
import { useEmailAdmin } from '../_Hooks/useEmailAdmin';
import { EMAIL_ADMIN_CONSTANTS as C } from '../_MODULES/_API';
import { confirm } from '../../../../../_CORE/APP/Templates/_UI/Confirm/AppConfirmHost';

const dayKey = (value) => String(value || '').slice(0, 10);
const norm = (value = '') => String(value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

export const EmailSuppressionsView = () => {
    const api = useEmailAdmin();
    const [items, setItems] = useState([]);
    const [search, setSearch] = useState('');
    const [date, setDate] = useState(null);
    const [range, setRange] = useState([null, null]);
    const [order, setOrder] = useState('created-desc');
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(12);
    const [email, setEmail] = useState('');
    const [reason, setReason] = useState('');
    const [message, setMessage] = useState('');

    const load = async () => {
        try { setItems(await api.get(C.SUPPRESSIONS) || []); setMessage(''); }
        catch (err) { setMessage(err.message); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

    const submit = async (event) => {
        event.preventDefault();
        try {
            await api.securePost(C.SUPPRESSION_SAVE, { email, reason });
            setEmail(''); setReason(''); await load();
        } catch (err) { setMessage(err.message); }
    };
    const remove = async (item) => {
        if (!(await confirm.warning(`Quitar ${item.email} de bloqueos?`, 'La direccion podra volver a entrar en futuras campanas.'))) return;
        try { await api.securePost(C.SUPPRESSION_REMOVE, { id: item.id }); await load(); }
        catch (err) { setMessage(err.message); }
    };

    const filtered = useMemo(() => {
        const term = norm(search);
        const exact = date instanceof Date ? `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}` : '';
        const [from, to] = range || [null, null];
        const fromTime = from instanceof Date ? new Date(from.getFullYear(), from.getMonth(), from.getDate()).getTime() : null;
        const toTime = to instanceof Date ? new Date(to.getFullYear(), to.getMonth(), to.getDate(), 23, 59, 59, 999).getTime() : null;
        const rows = items.filter((item) => {
            if (term && !norm(`${item.email} ${item.reason}`).includes(term)) return false;
            if (exact && dayKey(item.created_at) !== exact) return false;
            if (fromTime || toTime) {
                const current = new Date(item.created_at).getTime();
                if (Number.isNaN(current)) return false;
                if (fromTime && current < fromTime) return false;
                if (toTime && current > toTime) return false;
            }
            return true;
        });
        return [...rows].sort((a, b) => order === 'email-asc' ? String(a.email).localeCompare(String(b.email)) : new Date(b.created_at) - new Date(a.created_at));
    }, [items, search, date, range, order]);

    useEffect(() => { setPage(1); }, [search, date, range, order, perPage]);
    const totalPages = perPage === -1 ? 1 : Math.max(1, Math.ceil(filtered.length / perPage));
    const safePage = Math.min(page, totalPages);
    const visible = perPage === -1 ? filtered : filtered.slice((safePage - 1) * perPage, safePage * perPage);
    const from = filtered.length ? (perPage === -1 ? 1 : ((safePage - 1) * perPage) + 1) : 0;
    const to = filtered.length ? (perPage === -1 ? filtered.length : Math.min(safePage * perPage, filtered.length)) : 0;

    return (
        <EmailSectionLayout title="Bloqueos" description="Direcciones que no deben volver a entrar en ninguna cola de envío, aunque estén en contactos o listas.">
            <section className="PBADMIN__panel PBMAIL__panel">
                <div className="PBADMIN__panel_header"><div><h2>Agregar bloqueo</h2><p>Útil para bajas, rebotes permanentes o destinatarios que no querés contactar nuevamente.</p></div></div>
                <form className="PBMAIL__suppression_form" onSubmit={submit}>
                    <label className="PBADMIN__field">
                        <EmailFieldLabel status="required">Email</EmailFieldLabel>
                        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Ej: persona@empresa.com" />
                        <EmailFieldHint>Esta direccion no volvera a entrar en ninguna cola.</EmailFieldHint>
                    </label>
                    <label className="PBADMIN__field">
                        <EmailFieldLabel status="recommended">Motivo</EmailFieldLabel>
                        <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ej: pidio no recibir mas correos / rebote permanente" />
                        <EmailFieldHint>No es obligatorio, pero te ayuda a recordar por que fue bloqueada.</EmailFieldHint>
                    </label>
                    <button className="PBADMIN__primary" type="submit" disabled={api.isLoading}><FiShield /> Bloquear</button>
                </form>
            </section>

            <section className="PBADMIN__panel PBMAIL__panel">
                <div className="PBADMIN__collection_tools PBMAIL__collection_tools">
                    <TableHeader
                        pill="SEGURIDAD"
                        title="Lista de"
                        titleAccent="bloqueo"
                        hasNew={false}
                        search={search}
                        onSearch={setSearch}
                        searchPlaceholder="Buscar email o motivo…"
                        orderOptions={[{ value: 'created-desc', label: 'Más recientes' }, { value: 'email-asc', label: 'Email A → Z' }]}
                        order={order}
                        onOrder={setOrder}
                        filters={[
                            { key: 'date', label: 'Fecha exacta', type: 'date', value: date, onChange: setDate },
                            { key: 'range', label: 'Rango de fechas', type: 'dateRange', col: 2, value: range, onChange: (value) => setRange(value || [null, null]) },
                        ]}
                        onClearFilters={() => { setSearch(''); setDate(null); setRange([null, null]); }}
                        stats={<div className="PBADMIN__filter_summary"><span><strong>{filtered.length}</strong> bloqueado{filtered.length === 1 ? '' : 's'}</span><span>{items.length} total</span></div>}
                    />
                </div>

                {message && <div className="PBADMIN__alert">{message}</div>}
                <div className="PBMAIL__suppression_rows">
                    {visible.length === 0 && !api.isLoading && <div className="PBADMIN__empty">No hay direcciones bloqueadas.</div>}
                    {visible.map((item) => (
                        <article key={item.id}><FiShield /><div><strong>{item.email}</strong><span>{item.reason || 'Sin motivo registrado'}</span></div><time>{item.created_at}</time><button type="button" title="Quitar bloqueo" onClick={() => remove(item)}><FiTrash2 /></button></article>
                    ))}
                </div>
                {filtered.length > 0 && <div className="PBADMIN__collection_footer"><div className="PBADMIN__collection_count">Mostrando <strong>{from}–{to}</strong> de <strong>{filtered.length}</strong></div><TablePerPage value={perPage} onChange={setPerPage} totalItems={filtered.length} /><FooterPagination pagina={safePage} totalPaginas={totalPages} onIr={setPage} /></div>}
            </section>
        </EmailSectionLayout>
    );
};
